import type { ReactNode } from "react";
import { Link } from "react-router-dom";

interface ChallengeScoreCardProps {
  items: number;
  correct: number;
  averageResponseMs: number;
  /** Best accuracy (0..1) from previous attempts, null if first attempt. */
  previousBest: number | null;
  onAgain: () => void;
  onStop: () => void;
}

export function ChallengeScoreCard({
  items,
  correct,
  averageResponseMs,
  previousBest,
  onAgain,
  onStop,
}: ChallengeScoreCardProps): ReactNode {
  const accuracy = items > 0 ? correct / items : 0;
  const pct = Math.round(accuracy * 100);
  const isNewBest =
    previousBest === null || accuracy > previousBest;
  const prevPct =
    previousBest !== null ? Math.round(previousBest * 100) : null;

  return (
    <div className="max-w-xl mx-auto pt-8">
      <p className="font-mono text-xs uppercase tracking-widest text-accent mb-2">challenge</p>
      <h1 className="font-serif text-4xl font-medium text-ink mb-2">
        Session <em className="italic" style={{ color: "#872932" }}>complete</em>.
      </h1>

      {isNewBest && (
        <p className="font-mono text-[10px] uppercase tracking-widest mb-5" style={{ color: "#872932" }}>
          ★ New personal best
        </p>
      )}

      <div className="grid grid-cols-3 border border-ink/10 mb-6">
        <div className="p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-inkFaded mb-1">Correct</p>
          <p className="font-serif text-3xl font-medium text-ink">
            {correct}
            <span className="text-lg text-inkSoft">/{items}</span>
          </p>
        </div>
        <div className="p-5 border-l border-ink/10">
          <p className="font-mono text-[10px] uppercase tracking-widest text-inkFaded mb-1">Accuracy</p>
          <p className="font-serif text-3xl font-medium text-ink">
            {pct}
            <span className="font-serif italic text-lg text-inkSoft ml-0.5">%</span>
          </p>
        </div>
        <div className="p-5 border-l border-ink/10">
          <p className="font-mono text-[10px] uppercase tracking-widest text-inkFaded mb-1">Avg time</p>
          <p className="font-serif text-3xl font-medium text-ink">
            {(averageResponseMs / 1000).toFixed(1)}
            <span className="text-sm text-inkSoft ml-0.5">s</span>
          </p>
        </div>
      </div>

      {prevPct !== null && !isNewBest && (
        <p className="font-mono text-xs text-inkFaded mb-6">
          Personal best: {prevPct}%
        </p>
      )}

      <div className="flex gap-3 flex-wrap">
        <button
          type="button"
          onClick={onAgain}
          className="px-6 py-2.5 bg-ink text-paper hover:bg-accent transition-colors font-medium text-sm"
        >
          Try again →
        </button>
        <button
          type="button"
          onClick={onStop}
          className="px-6 py-2.5 bg-transparent border border-ink/20 text-ink hover:bg-ink hover:text-paper transition-colors text-sm"
        >
          Change settings
        </button>
        <Link
          to="/exercise"
          className="px-6 py-2.5 bg-transparent border border-ink/20 text-ink hover:bg-ink hover:text-paper transition-colors text-sm"
        >
          All drills
        </Link>
      </div>
    </div>
  );
}
